import { useMemo } from 'react';

import type {
  EstimationItemResourceResponse,
  EstimationModelResponse,
} from '@/api/schema/data-contracts';
import { useMessages } from '@/hooks/messages';
import type {
  CostModel,
  ResourceEstimationItemsTableRecord,
} from '@/pages/estimation/detail/model';

import HierarchicalManagementReportTable, {
  type HierarchicalTableRow,
} from '../components/HierarchicalManagementReportTable';
import { managementReportResourceCostBreakdownSummaryTableMessageDefinitions } from './messages';

export interface ManagementReportResourceCostBreakdownSummaryTableProps {
  resourceEstimationItems: ResourceEstimationItemsTableRecord[];
  allResourceEstimationItems: ResourceEstimationItemsTableRecord[];
  costs?: CostModel;
  estimationModel: EstimationModelResponse;
  assumptions?: Array<{
    id: string;
    estimationItemId: string;
    description: string;
    isValid: boolean;
  }>;
}

/**
 * EWM-1135: the item tree, indexed once.
 *
 * `getDescendantsRecursive` used to re-filter the full item array at every node it visited, and it
 * is run once per phase from two separate call sites - O(phases x items x depth), measured at 50 ms
 * on a 4k-item estimation. Children are collected in `allItems` order, same as the filter produced,
 * so the descendant ordering callers see is unchanged.
 */
interface ItemTreeIndex {
  childrenByParentId: ReadonlyMap<string, ResourceEstimationItemsTableRecord[]>;
  byId: ReadonlyMap<string, ResourceEstimationItemsTableRecord>;
}

const buildItemTreeIndex = (allItems: ResourceEstimationItemsTableRecord[]): ItemTreeIndex => {
  const childrenByParentId = new Map<string, ResourceEstimationItemsTableRecord[]>();
  const byId = new Map<string, ResourceEstimationItemsTableRecord>();

  for (const item of allItems) {
    byId.set(item.id, item);
    if (item.parentId == null) continue;
    const siblings = childrenByParentId.get(item.parentId);
    if (siblings) {
      siblings.push(item);
    } else {
      childrenByParentId.set(item.parentId, [item]);
    }
  }

  return { childrenByParentId, byId };
};

const getDescendantsRecursive = (
  parentId: string,
  index: ItemTreeIndex,
): ResourceEstimationItemsTableRecord[] => {
  const children = index.childrenByParentId.get(parentId) ?? [];
  return children.flatMap((child) => [child, ...getDescendantsRecursive(child.id, index)]);
};

const getAggregatedResourcesForPhase = (
  phaseId: string,
  index: ItemTreeIndex,
): EstimationItemResourceResponse[] => {
  const phase = index.byId.get(phaseId);
  const directResources = phase?.resources || [];

  const descendants = getDescendantsRecursive(phaseId, index);
  const descendantResources = descendants.flatMap((item) => item.resources || []);

  return [...directResources, ...descendantResources];
};

export const buildAggregatedPhaseItems = (
  phases: ResourceEstimationItemsTableRecord[],
  allItems: ResourceEstimationItemsTableRecord[],
  index: ItemTreeIndex = buildItemTreeIndex(allItems),
): ResourceEstimationItemsTableRecord[] => {
  return phases.map((phase) => ({
    ...phase,
    resources: getAggregatedResourcesForPhase(phase.id, index),
  }));
};

const getAggregatedAssumptionIdsForPhase = (phaseId: string, index: ItemTreeIndex): string[] => {
  const descendants = getDescendantsRecursive(phaseId, index);
  return [phaseId, ...descendants.map((item) => item.id)];
};

const ManagementReportResourceCostBreakdownSummaryTable: React.FC<
  ManagementReportResourceCostBreakdownSummaryTableProps
> = ({
  resourceEstimationItems = [],
  allResourceEstimationItems = [],
  estimationModel,
  costs,
  assumptions = [],
}: ManagementReportResourceCostBreakdownSummaryTableProps) => {
  const messages = useMessages(managementReportResourceCostBreakdownSummaryTableMessageDefinitions);

  // One index shared by both memos below: they depend on the same arrays, so they always
  // recompute together and would otherwise each rebuild an identical index.
  const itemTree = useMemo(
    () => buildItemTreeIndex(allResourceEstimationItems),
    [allResourceEstimationItems],
  );

  const aggregatedAssumptions = useMemo(() => {
    const phaseToAllItemIds = new Map<string, Set<string>>();
    resourceEstimationItems.forEach((phase) => {
      const relatedIds = getAggregatedAssumptionIdsForPhase(phase.id, itemTree);
      phaseToAllItemIds.set(phase.id, new Set(relatedIds));
    });

    const result: Array<{
      id: string;
      estimationItemId: string;
      description: string;
      isValid: boolean;
    }> = [];

    resourceEstimationItems.forEach((phase) => {
      const relatedIds = phaseToAllItemIds.get(phase.id);
      if (!relatedIds) return;

      const phaseAssumptions = assumptions.filter((assumption) =>
        relatedIds.has(assumption.estimationItemId),
      );

      phaseAssumptions.forEach((assumption) => {
        result.push({
          ...assumption,
          estimationItemId: phase.id,
        });
      });
    });

    return result;
  }, [resourceEstimationItems, itemTree, assumptions]);

  const aggregatedPhaseItems = useMemo(
    () => buildAggregatedPhaseItems(resourceEstimationItems, allResourceEstimationItems, itemTree),
    [resourceEstimationItems, allResourceEstimationItems, itemTree],
  );

  const tableConfig = useMemo(
    () => ({
      items: aggregatedPhaseItems,
      getChildrenByParent: () => [] as ResourceEstimationItemsTableRecord[],
      createRowFromItem: (
        item: ResourceEstimationItemsTableRecord,
        _counter: number,
        _subCounter?: number,
        level: number = 0,
      ) => {
        return {
          id: `resource-breakdown-summary-${item.id}`,
          reference: item.reference,
          name: item.name,
          description: item.description,
          isParent: false,
          level,
          estimationItemId: item.id,
          resourceEstimationItem: item,
        };
      },
      getEstimationItemForVisibility: (row: HierarchicalTableRow) => row.resourceEstimationItem,
      defaultGroupName: messages.projectPhase(),
      showEffortsColumn: true,
      getEffortValue: (row: HierarchicalTableRow) =>
        row.resourceEstimationItem?.calculatedResult ?? 0,
      emptyStateMessage: messages.emptyTableText(),
      hideExpandButton: true,
    }),
    [aggregatedPhaseItems, messages],
  );

  return (
    <HierarchicalManagementReportTable
      config={tableConfig}
      costs={costs}
      estimationModel={estimationModel}
      assumptions={aggregatedAssumptions}
      hiddenCostTypeColumns={[]}
      searchText=''
    />
  );
};

export default ManagementReportResourceCostBreakdownSummaryTable;
